import React, { useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import { CssBaseline, Paper, Typography, Link, Divider } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import LEGAL from "./legalIdentity";

/* ======================================================
   STYLES — coherentes con Login/Signup (fondo degradado
   + tarjeta blanca), pero optimizados para lectura larga.
====================================================== */
const useStyles = makeStyles((theme) => ({
  root: {
    // Scroll propio: el body de la app tiene overflow:hidden global (Login/style.css),
    // por eso la página legal debe ser su propio contenedor scrolleable.
    height: "100vh",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    background:
      "radial-gradient(1200px 500px at 20% 10%, rgba(59,130,246,0.18), transparent 60%)," +
      "radial-gradient(900px 400px at 80% 30%, rgba(16,185,129,0.12), transparent 55%)," +
      "linear-gradient(180deg, #f7f9ff 0%, #edf2ff 100%)",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
    padding: theme.spacing(4, 2),
    boxSizing: "border-box",
    color: "#0f172a",
  },
  paper: {
    width: "100%",
    maxWidth: 900,
    margin: "0 auto",
    borderRadius: 18,
    padding: theme.spacing(5),
    boxShadow: "0 18px 60px rgba(15, 23, 42, 0.10)",
    border: "1px solid rgba(15,23,42,0.06)",
    background: "rgba(255,255,255,0.96)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3, 2.5),
      borderRadius: 12,
    },
  },
  brand: {
    fontSize: 15,
    fontWeight: 900,
    color: "#2563eb",
    textDecoration: "none",
    letterSpacing: 0.4,
  },
  title: {
    fontSize: 30,
    fontWeight: 900,
    margin: theme.spacing(1.5, 0, 0.5),
    [theme.breakpoints.down("sm")]: { fontSize: 24 },
  },
  updated: {
    fontSize: 13,
    color: "rgba(15,23,42,0.55)",
    marginBottom: theme.spacing(3),
  },
  intro: {
    fontSize: 14.5,
    lineHeight: 1.7,
    color: "rgba(15,23,42,0.82)",
    marginBottom: theme.spacing(2),
  },
  footer: {
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    fontSize: 13,
    color: "rgba(15,23,42,0.6)",
    lineHeight: 1.7,
  },
  footerLinks: {
    marginTop: theme.spacing(1),
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  footerLink: {
    color: "#2563eb",
    fontWeight: 700,
    textDecoration: "none",
  },
  /* Sección reutilizable */
  section: { marginBottom: theme.spacing(3) },
  h2: {
    fontSize: 18,
    fontWeight: 800,
    margin: theme.spacing(0, 0, 1),
    color: "#0f172a",
  },
  body: {
    fontSize: 14.5,
    lineHeight: 1.75,
    color: "rgba(15,23,42,0.82)",
    "& p": { margin: theme.spacing(0, 0, 1.2) },
    "& ul": { margin: theme.spacing(0.5, 0, 1.2), paddingLeft: theme.spacing(3) },
    "& li": { marginBottom: 6 },
    "& a": { color: "#2563eb", fontWeight: 600 },
    "& strong": { color: "#0f172a" },
  },
}));

/**
 * Bloque de sección reutilizable (título + cuerpo). Acepta `id` para anclas
 * (p. ej. la sección de eliminación de datos que exige Meta).
 */
export const Section = ({ id, title, children }) => {
  const classes = useStyles();
  return (
    <section id={id} className={classes.section}>
      <Typography component="h2" className={classes.h2}>
        {title}
      </Typography>
      <div className={classes.body}>{children}</div>
    </section>
  );
};

/**
 * Carcasa común de las páginas legales: cabecera con marca + título + fecha,
 * cuerpo (children) y pie con la identidad del responsable y enlaces cruzados.
 */
const LegalLayout = ({ title, intro, children }) => {
  const classes = useStyles();

  // Documento legal → siempre inicia arriba al abrir.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={classes.root}>
      <CssBaseline />
      <Paper className={classes.paper}>
        <Link component={RouterLink} to="/login" className={classes.brand}>
          {LEGAL.brand}
        </Link>

        <Typography component="h1" className={classes.title}>
          {title}
        </Typography>
        <Typography className={classes.updated}>
          Última actualización: {LEGAL.lastUpdated}
        </Typography>

        {intro && <Typography className={classes.intro}>{intro}</Typography>}

        {children}

        <Divider />
        <div className={classes.footer}>
          <div>
            <strong>{LEGAL.brand}</strong> es operado por {LEGAL.operator}
            {LEGAL.tradeName ? ` (${LEGAL.tradeName})` : ""}, RUC {LEGAL.ruc},
            con domicilio en {LEGAL.address}.
          </div>
          <div>
            Contacto: <a href={`mailto:${LEGAL.email}`}>{LEGAL.email}</a>
          </div>
          <div className={classes.footerLinks}>
            <Link component={RouterLink} to="/privacy" className={classes.footerLink}>
              Política de Privacidad
            </Link>
            <Link component={RouterLink} to="/terms" className={classes.footerLink}>
              Condiciones de Uso
            </Link>
            <Link component={RouterLink} to="/login" className={classes.footerLink}>
              Volver al inicio
            </Link>
          </div>
        </div>
      </Paper>
    </div>
  );
};

export default LegalLayout;
